import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../services/apiHelpers';

/**
 * PaymentMethodModal - Premium Healthcare Payment Interface
 * Modern Design System: Glassmorphism + Gradient Accents + Medical Trust Colors
 */

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
};

export default function PaymentMethodModal({
  patientName,
  consultationFeeAssigned,
  onConfirm,
  onCancel,
  isLoading = false,
  appointmentId,
  clinicId,
  appointmentNumber
}) {
  const [selectedMethod, setSelectedMethod] = useState('CASH');
  const [paidAmount, setPaidAmount] = useState(consultationFeeAssigned ?? '');
  const [notes, setNotes] = useState('');
  const [upiUri, setUpiUri] = useState(null);
  const [upiPayeeName, setUpiPayeeName] = useState(null);
  const [upiId, setUpiId] = useState(null);
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);
  const [isQrFullscreen, setIsQrFullscreen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { auth } = useAuth();
  const resolvedClinicId = clinicId || auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__);

  const handleGenerateUpiQr = useCallback(async () => {
    try {
      if (!appointmentId) throw new Error('Appointment ID reference is missing');
      if (!resolvedClinicId) throw new Error('Clinic context profile is missing');

      setIsGeneratingQr(true);
      const res = await fetch(
        buildApiUrl(`payment-utils/upi-uri/${appointmentId}?amount=${encodeURIComponent(Number(paidAmount || consultationFeeAssigned || 0).toFixed(2))}`),
        { headers: { 'X-Clinic-Id': resolvedClinicId } }
      );

      const payload = await res.json();
      if (!res.ok || !payload.success) {
        throw new Error(payload.message || 'Failed to initialize terminal network payload');
      }

      setUpiUri(payload.upiUri);
      setUpiPayeeName(payload.payeeName || 'Healthcare Provider');
      setUpiId(payload.upiId);
    } catch (error) {
      console.error('QR Generator Error:', error);
      alert('Unable to generate UPI QR: ' + (error.message || error));
    } finally {
      setIsGeneratingQr(false);
    }
  }, [appointmentId, consultationFeeAssigned, paidAmount, resolvedClinicId]);

  useEffect(() => {
    setPaidAmount(consultationFeeAssigned ?? '');
  }, [consultationFeeAssigned]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (selectedMethod === 'UPI') {
        setUpiUri(null);
        setUpiPayeeName(null);
        setUpiId(null);
        await handleGenerateUpiQr();
      } else {
        setUpiUri(null);
        setUpiPayeeName(null);
        setUpiId(null);
        setIsGeneratingQr(false);
      }
    };
    if (mounted) run();
    return () => { mounted = false; };
  }, [selectedMethod, paidAmount, handleGenerateUpiQr]);

  const handleConfirm = () => {
    if (selectedMethod === 'CASH' && (!paidAmount || parseFloat(paidAmount) <= 0)) {
      alert('Please enter a valid amount');
      return;
    }
    if (selectedMethod === 'UPI' && !upiUri) {
      alert('Please wait for the UPI QR code to load before confirming');
      return;
    }

    setIsSuccess(true);

    setTimeout(() => {
      onConfirm({
        paymentMethod: selectedMethod,
        paidAmount: parseFloat(selectedMethod === 'UPI' ? consultationFeeAssigned : paidAmount),
        notes
      });
    }, 2800);
  };

  const handlePayViaUpi = () => {
    if (!upiUri) {
      alert('Please wait for the UPI payment link to load');
      return;
    }

    window.location.href = upiUri;
  };

  const paymentMethods = [
    {
      id: 'CASH',
      label: 'Cash Payment',
      description: 'Instant physical currency'
    },
    {
      id: 'UPI',
      label: 'Digital Payment',
      description: 'Secure UPI transfer'
    }
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@600;700&display=swap');

        * { box-sizing: border-box; }

        /* ══════════════════════════════════════════════════════════════ */
        /* MODAL BACKDROP & CONTAINER */
        /* ══════════════════════════════════════════════════════════════ */

        .payment-modal-backdrop {
          position: fixed;
          inset: 0;
          background: linear-gradient(135deg, rgba(30, 64, 175, 0.15) 0%, rgba(15, 23, 42, 0.25) 100%);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          z-index: 9998;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          animation: backdropFadeIn 0.35s ease-out;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        @keyframes backdropFadeIn { 
          from { opacity: 0; } 
          to { opacity: 1; } 
        }

        .payment-modal-container {
          position: relative;
          width: 100%;
          max-width: 800px;
          background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border-radius: 24px;
          box-shadow: 
            0 0 0 1px rgba(255, 255, 255, 0.8) inset,
            0 0 40px rgba(30, 64, 175, 0.08),
            0 20px 60px -20px rgba(15, 23, 42, 0.15);
          overflow: hidden;
          max-height: calc(100vh - 32px);
          display: flex;
          flex-direction: column;
          animation: modalSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 9999;
          transition: max-width 0.35s cubic-bezier(0.16, 1, 0.3, 1);
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        .payment-modal-container.upi-compact {
          max-width: 540px;
        }

        @keyframes modalSlideUp {
          from { 
            opacity: 0; 
            transform: translateY(24px) scale(0.98); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0) scale(1); 
          }
        }

        .modal-scroll-area {
          overflow-y: auto;
          flex: 1;
          scroll-behavior: smooth;
        }

        .modal-scroll-area::-webkit-scrollbar {
          width: 6px;
        }

        .modal-scroll-area::-webkit-scrollbar-track {
          background: transparent;
        }

        .modal-scroll-area::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.2);
          border-radius: 3px;
        }

        /* ══════════════════════════════════════════════════════════════ */
        /* HEADER SECTION */
        /* ══════════════════════════════════════════════════════════════ */

        .modal-header-section {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 251, 255, 0.8) 100%);
          padding: 28px 28px 18px;
          position: relative;
          border-bottom: 1px solid rgba(59, 130, 246, 0.1);
          backdrop-filter: blur(10px);
        }

        .header-top {
          display: flex;
          align-items: flex-start;
          justify-content: flex-start;
          position: relative;
          z-index: 2;
          gap: 16px;
        }

        .header-badge {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(30, 64, 175, 0.12) 0%, rgba(59, 130, 246, 0.08) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1e40af;
          border: 1.5px solid rgba(59, 130, 246, 0.25);
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(30, 64, 175, 0.08);
        }

        .header-close-btn {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border: 1px solid rgba(59, 130, 246, 0.15);
          color: #64748b;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          font-weight: 600;
          flex-shrink: 0;
          position: absolute;
          top: 28px;
          right: 28px;
        }

        .header-close-btn:hover:not(:disabled) { 
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          border-color: #fca5a5;
          transform: scale(1.08);
        }

        .header-text h2 { 
          font-size: 18px; 
          font-weight: 700; 
          color: #0f172a; 
          margin: 0 0 3px 0; 
          letter-spacing: -0.4px;
        }

        .header-text p { 
          font-size: 12px; 
          color: #64748b; 
          margin: 0; 
          font-weight: 500; 
        }

        .header-summary { 
          display: flex; 
          flex-wrap: wrap; 
          gap: 10px; 
          margin-top: 14px; 
          position: relative; 
          z-index: 2; 
        }

        .header-summary-item { 
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(30, 64, 175, 0.04) 100%);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 10px; 
          padding: 6px 14px; 
          font-size: 12px; 
          color: #1e40af; 
          font-weight: 600;
          backdrop-filter: blur(10px);
        }

        /* ══════════════════════════════════════════════════════════════ */
        /* FORM LAYOUT & STYLING */
        /* ══════════════════════════════════════════════════════════════ */

        .modal-body {
          padding: 28px;
          display: grid;
          grid-template-columns: 1.15fr 0.85fr;
          gap: 28px;
          align-items: start;
        }

        .modal-body.full-width {
          grid-template-columns: 1fr !important;
        }

        .form-segment { 
          display: flex; 
          flex-direction: column; 
          gap: 18px; 
        }

        .form-group { 
          display: flex; 
          flex-direction: column; 
          gap: 8px; 
        }

        .pmm-label-header { 
          font-size: 11px; 
          font-weight: 700; 
          color: #1e40af; 
          text-transform: uppercase; 
          letter-spacing: 1px; 
          display: flex;
          align-items: center;
          gap: 6px;
        }

        /* ══════════════════════════════════════════════════════════════ */
        /* PAYMENT METHOD CARDS */
        /* ══════════════════════════════════════════════════════════════ */

        .payment-method-card {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-width: 0;
          padding: 12px 8px;
          border: 2px solid rgba(203, 213, 225, 0.5);
          border-radius: 14px;
          background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          user-select: none;
          position: relative;
          overflow: hidden;
          font: inherit;
          text-align: center;
        }

        .payment-method-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0) 0%, rgba(59, 130, 246, 0) 100%);
          opacity: 0;
          transition: opacity 0.25s ease;
        }

        .payment-method-card:hover { 
          border-color: rgba(59, 130, 246, 0.3);
          background: linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%);
          box-shadow: 0 8px 24px rgba(30, 64, 175, 0.06);
          transform: translateY(-2px);
        }

        .payment-method-card input[type="radio"] {
          display: none;
        }

        .payment-method-card.selected {
          border-color: #1e40af;
          background: linear-gradient(135deg, rgba(30, 64, 175, 0.06) 0%, rgba(59, 130, 246, 0.04) 100%);
          box-shadow: 
            0 0 0 4px rgba(59, 130, 246, 0.1),
            0 8px 24px rgba(30, 64, 175, 0.12);
        }

        .method-icon { 
          width: 30px; 
          height: 30px; 
          border-radius: 11px; 
          background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1.5px solid rgba(203, 213, 225, 0.6);
          display: flex; 
          align-items: center; 
          justify-content: center; 
          color: #64748b; 
          flex-shrink: 0;
          transition: all 0.25s ease;
        }

        .payment-method-card.selected .method-icon { 
          background: linear-gradient(135deg, rgba(30, 64, 175, 0.12) 0%, rgba(59, 130, 246, 0.08) 100%);
          border-color: rgba(30, 64, 175, 0.25);
          color: #1e40af;
          box-shadow: 0 4px 12px rgba(30, 64, 175, 0.12);
        }

        .method-name { 
          font-size: 12px; 
          font-weight: 700; 
          color: #0f172a; 
          margin: 0; 
        }

        .method-desc { 
          font-size: 12px; 
          color: #64748b; 
          margin: 2px 0 0 0; 
          font-weight: 500;
        }

        .payment-method-options {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
        }

        @media (max-width: 480px) {
          .payment-method-options { gap: 6px; }
          .payment-method-card { gap: 4px; padding: 10px 4px; }
          .method-icon { width: 26px; height: 26px; }
          .method-name { font-size: 11px; }
        }

        /* ══════════════════════════════════════════════════════════════ */
        /* INPUT FIELDS & AMOUNTS */
        /* ══════════════════════════════════════════════════════════════ */

        .amount-input-wrapper { 
          position: relative; 
          display: flex; 
          align-items: center; 
        }

        .amount-currency { 
          position: absolute; 
          left: 16px; 
          font-size: 20px; 
          font-weight: 700; 
          color: #059669; 
          user-select: none; 
        }

        .amount-input { 
          width: 100%; 
          padding: 14px 16px 14px 40px; 
          border: 2px solid rgba(203, 213, 225, 0.6);
          border-radius: 12px; 
          font-size: 18px; 
          font-weight: 700; 
          font-family: 'JetBrains Mono', monospace; 
          color: #0f172a;
          background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          outline: none; 
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .amount-input:focus { 
          border-color: #1e40af;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1), 0 8px 20px rgba(30, 64, 175, 0.1);
        }

        .amount-input::placeholder {
          color: #cbd5e1;
        }

        .fee-badge { 
          display: flex; 
          align-items: center; 
          gap: 7px; 
          padding: 7px 13px; 
          background: linear-gradient(135deg, rgba(5, 150, 105, 0.08) 0%, rgba(5, 150, 105, 0.04) 100%);
          border: 1.5px solid rgba(5, 150, 105, 0.2);
          border-radius: 10px; 
          font-size: 12px; 
          color: #047857; 
          font-weight: 600; 
          margin-top: 6px;
          backdrop-filter: blur(10px);
        }

        .remarks-textarea { 
          width: 100%; 
          padding: 13px 14px; 
          border: 2px solid rgba(203, 213, 225, 0.6);
          border-radius: 12px; 
          font-size: 13px; 
          color: #334155; 
          font-family: 'Plus Jakarta Sans', sans-serif;
          outline: none; 
          resize: none; 
          min-height: 90px; 
          line-height: 1.5;
          background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .remarks-textarea:focus { 
          border-color: #1e40af;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1), 0 8px 20px rgba(30, 64, 175, 0.1);
        }

        .remarks-textarea::placeholder {
          color: #cbd5e1;
        }

        /* ══════════════════════════════════════════════════════════════ */
        /* UPI QR CODE SECTION */
        /* ══════════════════════════════════════════════════════════════ */

        .upi-qr-automated-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 2px dashed rgba(59, 130, 246, 0.2);
          border-radius: 16px;
          padding: 28px 20px;
          position: relative;
          overflow: hidden;
          animation: qrTargetPop 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        @keyframes qrTargetPop { 
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(8px); 
          } 
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          } 
        }

        .qr-image-wrapper-husk {
          position: relative;
          background: linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%);
          padding: 12px;
          border-radius: 14px;
          border: 2px solid rgba(59, 130, 246, 0.2);
          box-shadow: 
            0 0 0 1px rgba(255, 255, 255, 0.8) inset,
            0 8px 24px rgba(30, 64, 175, 0.1);
          overflow: hidden;
        }

        .qr-image-node {
          width: 180px;
          height: 180px;
          display: block;
          cursor: zoom-in;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .qr-image-node:hover {
          filter: brightness(1.05);
          transform: scale(1.02);
        }

        .qr-image-wrapper-husk::after {
          content: '';
          position: absolute;
          left: 0; 
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, rgba(30, 64, 175, 0.6), transparent);
          box-shadow: 0 0 12px rgba(30, 64, 175, 0.4);
          animation: upiLaserScan 2.2s ease-in-out infinite;
        }

        @keyframes upiLaserScan {
          0% { top: 0%; opacity: 0; }
          50% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }

        .upi-qr-automated-container > div:first-child {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(59, 130, 246, 0.1);
        }

        .upi-info-section {
          width: 100%;
          text-align: center;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px dashed rgba(59, 130, 246, 0.15);
        }

        .qr-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: center;
          margin-top: 18px;
        }

        .btn-qr-cancel { 
          padding: 10px 18px; 
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          color: #475569; 
          border: 1.5px solid rgba(203, 213, 225, 0.6);
          border-radius: 10px; 
          font-size: 13px; 
          font-weight: 600; 
          cursor: pointer; 
        }

        .btn-qr-confirm { 
          flex: 1 1 150px;
          padding: 10px 20px; 
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: #ffffff; 
          border: none; 
          border-radius: 10px; 
          font-size: 13px; 
          font-weight: 700; 
          cursor: pointer; 
        }

        .btn-qr-confirm:disabled, .btn-qr-cancel:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 520px) {
          .qr-actions > button {
            flex: 1 1 100%;
          }
        }

        .qr-amount-display {
          text-align: right;
        }

        .qr-amount-value {
          font-size: 18px;
          font-weight: 800;
          color: #1e40af;
          font-family: "'JetBrains Mono', monospace";
        }

        .qr-amount-label {
          font-size: 10px;
          font-weight: 700;
          color: #059669;
          text-transform: uppercase;
          margin-top: 2px;
          letter-spacing: 0.6px;
        }

        .qr-payee-name {
          font-size: 13px;
          font-weight: 700;
          color: #1e293b;
          margin-top: 4px;
        }

        .qr-payee-id {
          font-size: 11px;
          color: #94a3b8;
          font-family: "'JetBrains Mono', monospace";
          margin-top: 3px;
          word-break: break-all;
        }

        /* Loading State */
        .qr-loading-state {
          text-align: center;
          padding: 40px 0;
          color: #64748b;
        }

        .spinner {
          display: inline-block;
          width: 28px;
          height: 28px;
          border: 3px solid rgba(59, 130, 246, 0.2);
          border-top-color: #1e40af;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 12px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .qr-loading-text {
          font-size: 13px;
          font-weight: 600;
          margin-top: 8px;
        }

        .qr-error-state {
          font-size: 12.5px;
          color: #dc2626;
          font-weight: 600;
          padding: 20px 0;
        }

        /* Fullscreen QR */
        .qr-fullscreen-overlay { 
          position: fixed; 
          inset: 0; 
          background: linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 64, 175, 0.15) 100%);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          z-index: 10000; 
          display: flex; 
          align-items: center; 
          justify-content: center;
          animation: overlayFadeIn 0.3s ease-out;
        }

        @keyframes overlayFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .qr-fullscreen-card { 
          background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          padding: 28px; 
          border-radius: 20px; 
          display: flex; 
          flex-direction: column; 
          align-items: center; 
          gap: 18px; 
          position: relative; 
          border: 1px solid rgba(59, 130, 246, 0.2);
          box-shadow: 0 25px 50px rgba(15, 23, 42, 0.25);
        }

        .qr-fullscreen-card .header-close-btn {
          position: absolute;
          top: 16px;
          right: 16px;
        }

        .qr-fullscreen-card img {
          width: 360px;
          height: 360px;
          border-radius: 12px;
          border: 2px solid rgba(59, 130, 246, 0.15);
          box-shadow: 0 8px 24px rgba(30, 64, 175, 0.12);
        }

        .qr-fullscreen-exit-hint {
          color: #64748b;
          font-size: 13px;
          text-align: center;
          font-weight: 500;
        }

        /* ══════════════════════════════════════════════════════════════ */
        /* SUCCESS STATE */
        /* ══════════════════════════════════════════════════════════════ */

        .pmm-success-overlay { 
          position: absolute; 
          inset: 0; 
          background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border-radius: 24px; 
          z-index: 100; 
          display: flex; 
          flex-direction: column; 
          align-items: center; 
          justify-content: center; 
          padding: 24px;
          animation: successOverlayFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        @keyframes successOverlayFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .success-circle-husk { 
          width: 88px; 
          height: 88px; 
          background: linear-gradient(135deg, rgba(5, 150, 105, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%);
          border-radius: 50%; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          position: relative; 
          margin-bottom: 20px;
          animation: ringScale 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
          border: 2px solid rgba(5, 150, 105, 0.2);
          box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6) inset;
        }

        @keyframes ringScale { 
          from { 
            transform: scale(0.5); 
            opacity: 0; 
          } 
          to { 
            transform: scale(1); 
            opacity: 1; 
          } 
        }

        .success-circle-husk::after { 
          content: ''; 
          position: absolute; 
          inset: -6px; 
          border-radius: 50%; 
          border: 2px solid #059669; 
          opacity: 0.3; 
          animation: pulseBlast 1.6s cubic-bezier(0.16, 1, 0.3, 1) infinite; 
        }

        @keyframes pulseBlast { 
          0% { 
            transform: scale(1); 
            opacity: 0.6; 
          } 
          100% { 
            transform: scale(1.4); 
            opacity: 0; 
          } 
        }

        .success-stagger-text { 
          opacity: 0; 
          transform: translateY(12px); 
          animation: textRise 0.45s cubic-bezier(0.16, 1, 0.3, 1) both; 
        }

        .success-stagger-text:nth-child(2) { 
          animation-delay: 0.35s; 
        }

        .success-stagger-text:nth-child(3) { 
          animation-delay: 0.55s; 
        }

        @keyframes textRise { 
          to { 
            opacity: 1; 
            transform: translateY(0); 
          } 
        }

        .success-title {
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
          letter-spacing: -0.3px;
        }

        .success-subtitle {
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
          margin: 0;
        }

        .success-receipt-card {
          background: linear-gradient(135deg, rgba(5, 150, 105, 0.06) 0%, rgba(5, 150, 105, 0.03) 100%);
          border: 1.5px dashed rgba(5, 150, 105, 0.25);
          border-radius: 12px;
          padding: 14px 18px;
          width: 100%;
          max-width: 300px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 4px;
        }

        .success-receipt-label {
          font-size: 11px;
          font-weight: 700;
          color: #047857;
          text-transform: uppercase;
          letter-spacing: 0.8px;
        }

        .success-receipt-amount {
          font-size: 17px;
          font-weight: 800;
          color: #059669;
          font-family: "'JetBrains Mono', monospace";
        }

        /* ══════════════════════════════════════════════════════════════ */
        /* FOOTER & BUTTONS */
        /* ══════════════════════════════════════════════════════════════ */

        .modal-footer { 
          padding: 16px 28px; 
          background: linear-gradient(135deg, rgba(248, 251, 255, 0.95) 0%, rgba(240, 247, 255, 0.8) 100%);
          border-top: 1px solid rgba(59, 130, 246, 0.1); 
          display: flex; 
          gap: 12px; 
          justify-content: flex-end;
        }

        .btn-cancel { 
          padding: 11px 24px; 
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          color: #475569; 
          border: 1.5px solid rgba(203, 213, 225, 0.6);
          border-radius: 11px; 
          font-size: 13.5px; 
          font-weight: 600; 
          cursor: pointer; 
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .btn-cancel:hover:not(:disabled) { 
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          color: #1e293b;
          border-color: rgba(203, 213, 225, 0.8);
          transform: translateY(-1px);
        }

        .btn-confirm { 
          padding: 11px 32px; 
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: #ffffff; 
          border: none; 
          border-radius: 11px; 
          font-size: 13.5px; 
          font-weight: 700; 
          cursor: pointer; 
          display: flex; 
          align-items: center; 
          gap: 8px; 
          box-shadow: 0 6px 20px rgba(5, 150, 105, 0.25);
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .btn-confirm:hover:not(:disabled) { 
          background: linear-gradient(135deg, #047857 0%, #065f46 100%);
          box-shadow: 0 8px 28px rgba(5, 150, 105, 0.35);
          transform: translateY(-2px);
        }

        .btn-confirm:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .checkmark-icon {
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: dashCheck 0.5s ease-out 0.3s forwards;
        }

        @keyframes dashCheck {
          to {
            stroke-dashoffset: 0;
          }
        }

        /* ══════════════════════════════════════════════════════════════ */
        /* RESPONSIVE DESIGN */
        /* ══════════════════════════════════════════════════════════════ */

        @media (max-width: 768px) {
          .payment-modal-container, 
          .payment-modal-container.upi-compact { 
            max-width: 460px; 
          }

          .modal-body { 
            grid-template-columns: 1fr !important; 
            gap: 20px; 
            padding: 20px; 
          }

          .modal-header-section {
            padding: 20px 20px 14px;
          }

          .modal-footer { 
            padding: 12px 20px;
            gap: 10px;
          }

          .btn-cancel, 
          .btn-confirm { 
            flex: 1; 
            text-align: center; 
            justify-content: center; 
            padding: 10px 16px;
          }

          .header-text h2 {
            font-size: 16px;
          }

          .qr-image-node {
            width: 160px;
            height: 160px;
          }

          .qr-fullscreen-card img {
            width: 300px;
            height: 300px;
          }
        }

        @media (max-width: 480px) {
          .payment-modal-container {
            max-width: calc(100% - 16px);
            border-radius: 18px;
          }

          .header-top {
            flex-direction: column;
            align-items: flex-start;
          }

          .header-close-btn {
            position: absolute;
            right: 20px;
            top: 20px;
          }

          .modal-body {
            padding: 16px;
          }

          .modal-header-section {
            padding-right: 50px;
          }
        }
      `}</style>

      <div className="payment-modal-backdrop" onClick={onCancel}>
        <div 
          className={`payment-modal-container ${selectedMethod === 'UPI' ? 'upi-compact' : ''}`} 
          onClick={(e) => e.stopPropagation()}
        >
          
          {/* SUCCESS ANIMATION LAYER */}
          {isSuccess && (
            <div className="pmm-success-overlay">
              <div className="success-circle-husk">
                <svg 
                  className="checkmark-icon"
                  width="44" 
                  height="44" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="#059669" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div className="success-stagger-text">
                <h3 className="success-title">Payment Successful!</h3>
              </div>
              <div className="success-stagger-text">
                <p className="success-subtitle">Transaction processed and recorded</p>
              </div>
              <div className="success-stagger-text success-receipt-card">
                <span className="success-receipt-label">{selectedMethod} Receipt</span>
                <span className="success-receipt-amount">
                  {formatCurrency(selectedMethod === 'UPI' ? consultationFeeAssigned : paidAmount)}
                </span>
              </div>
            </div>
          )}

          {/* ACTIVE CONTENT LAYER */}
          <div>
            <div className="modal-header-section">
              <div className="header-top">
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div className="header-badge">
                    <svg 
                      width="22" 
                      height="22" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2.2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="5" width="20" height="14" rx="2" />
                      <line x1="2" y1="10" x2="22" y2="10" />
                    </svg>
                  </div>
                  <div className="header-text">
                    <h2>Collect Payment</h2>
                    <p>Secure transaction processing</p>
                  </div>
                </div>
              </div>

              <button 
                className="header-close-btn" 
                onClick={onCancel} 
                disabled={isLoading}
                title="Close"
              >
                ✕
              </button>

              {(patientName || appointmentNumber) && (
                <div className="header-summary">
                  {patientName && <div className="header-summary-item">{patientName}</div>}
                  {appointmentNumber && <div className="header-summary-item">Token #{appointmentNumber}</div>}
                </div>
              )}
            </div>

            <div className="modal-scroll-area">
              <div className={`modal-body ${selectedMethod === 'UPI' ? 'full-width' : ''}`}>
                
                {/* LEFT SEGMENT — PAYMENT METHOD & QR */}
                <div className="form-segment">
                  <div>
                    <div className="pmm-label-header">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="12" cy="12" r="1" /></svg>
                      Payment Method
                    </div>
                    <div className="payment-method-options">
                      {paymentMethods.map((method) => {
                        const isSelected = selectedMethod === method.id;
                        return (
                          <button
                            key={method.id} 
                            type="button"
                            className={`payment-method-card ${isSelected ? 'selected' : ''}`} 
                            aria-pressed={isSelected}
                            onClick={() => setSelectedMethod(method.id)}
                          >
                            <div className="method-icon">
                              {method.id === 'CASH' ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="2" y="6" width="20" height="12" rx="2" />
                                  <circle cx="12" cy="12" r="2" />
                                </svg>
                              ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="5" y="2" width="14" height="20" rx="2" />
                                  <line x1="12" y1="18" x2="12.01" y2="18" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <p className="method-name">{method.label.replace(' Payment', '')}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* UPI QR SECTION */}
                  {selectedMethod === 'UPI' && (
                    <div className="upi-qr-automated-container">
                      {isGeneratingQr ? (
                        <div className="qr-loading-state">
                          <div className="spinner"></div>
                          <div className="qr-loading-text">Generating UPI QR Code...</div>
                        </div>
                      ) : upiUri ? (
                        <>
                          <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>Scan to Pay</div>
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', fontWeight: 500 }}>Ready for payment</div>
                            </div>
                            <div className="qr-amount-display">
                              <div className="qr-amount-value">{formatCurrency(consultationFeeAssigned)}</div>
                              <div className="qr-amount-label">Consultation Fee</div>
                            </div>
                          </div>
                          
                          <div className="qr-image-wrapper-husk">
                            <img 
                              className="qr-image-node" 
                              alt="UPI Payment QR" 
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(upiUri)}`} 
                              onClick={() => setIsQrFullscreen(true)} 
                            />
                          </div>
                          
                          <div className="upi-info-section">
                            <div className="qr-payee-name">{upiPayeeName}</div>
                            <div className="qr-payee-id">{upiId}</div>
                          </div>
                          <div className="qr-actions">
                            <button className="btn-qr-cancel" onClick={onCancel} disabled={isLoading}>Cancel</button>
                            <button className="btn-qr-confirm" onClick={handlePayViaUpi} disabled={isLoading || !upiUri}>
                              Pay via UPI app
                            </button>
                            <button className="btn-qr-confirm" onClick={handleConfirm} disabled={isLoading || !upiUri}>Confirm Payment</button>
                          </div>
                        </>
                      ) : (
                        <div className="qr-error-state">Unable to generate QR code. Please try again.</div>
                      )}
                    </div>
                  )}
                </div>

                {/* RIGHT SEGMENT — CASH AMOUNT & NOTES */}
                {selectedMethod !== 'UPI' && (
                  <div className="form-segment">
                    <div>
                      <div className="pmm-label-header">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="12" cy="12" r="1" /></svg>
                        Amount Collected
                      </div>
                      <div className="amount-input-wrapper">
                        <div className="amount-currency">₹</div>
                        <input 
                          type="number" 
                          className="amount-input" 
                          value={paidAmount} 
                          onChange={(e) => setPaidAmount(e.target.value)} 
                          placeholder="0" 
                          disabled={isLoading} 
                        />
                      </div>
                      {consultationFeeAssigned && (
                        <div className="fee-badge">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="16" />
                            <line x1="8" y1="12" x2="16" y2="12" />
                          </svg>
                          <span>Assigned Fee: {formatCurrency(consultationFeeAssigned)}</span>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <div className="pmm-label-header">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="12" cy="12" r="1" /></svg>
                        Remarks <span style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 500, textTransform: 'none', letterSpacing: 'normal' }}>(Optional)</span>
                      </div>
                      <textarea 
                        className="remarks-textarea" 
                        value={notes} 
                        onChange={(e) => setNotes(e.target.value)} 
                        placeholder="Add any payment notes or remarks..." 
                        disabled={isLoading} 
                      />
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="modal-footer">
            <button 
              className="btn-cancel" 
              onClick={onCancel} 
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              className="btn-confirm" 
              onClick={handleConfirm} 
              disabled={isLoading || (selectedMethod === 'UPI' && !upiUri)}
            >
              {isLoading && <span className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />}
              Confirm Payment
            </button>
          </div>
        </div>
      </div>

      {/* FULLSCREEN QR OVERLAY */}
      {isQrFullscreen && upiUri && (
        <div className="qr-fullscreen-overlay" onClick={() => setIsQrFullscreen(false)}>
          <div className="qr-fullscreen-card" onClick={(e) => e.stopPropagation()}>
            <button 
              className="header-close-btn" 
              style={{ position: 'absolute', top: '16px', right: '16px' }} 
              onClick={() => setIsQrFullscreen(false)}
              title="Close"
            >
              ✕
            </button>
            <img 
              alt="UPI QR Fullscreen" 
              src={`https://api.qrserver.com/v1/create-qr-code/?size=420x420&data=${encodeURIComponent(upiUri)}`} 
            />
            <span className="qr-fullscreen-exit-hint">Tap anywhere outside the code to close</span>
          </div>
        </div>
      )}
    </>
  );
}